import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { images } from "@img/imagesMap";
import useNurses from "@hooks/useNurses";
import type { NurseWithRelation } from "@vTypes/nurse-type";
import {
  linkPreceptorRelations,
  unlinkPreceptorRelations,
} from "@utils/nurse-util";
import CustomModal from "@/components/modal/CustomModal";
import { useIsAdmin, useLoginUser } from "@hooks/useAuth";
import { MSG } from "@constants/MSG.ts";
import { toast } from "@/components/toast/CustomToast";
import { ManagerTable } from "@/pages/roster-management/components/ManagerTable";
import { saveChangeFullNurseSchema } from "@script/mutate";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { SearchInput } from "@/components/common/SearchInput";
import { compareNurses } from "@/utils/sort-util";
import { COLUMN_WIDTHS } from "@/pages/roster-management/components/rosterTable.config";
import type { ShiftReturnType } from "@/hooks/roster-craete/useShiftsQuery";
import type { NurseResponse } from "@/types/nurse-type";
import {
  usePrecepteePeriodsQuery,
  type PrecepteePeriodItem,
} from "@/hooks/roster-craete/usePrecepteePeriodsQuery";
import { applyPrecepteePeriodsToNurses } from "@/utils/precepteePolicy";
import {
  LargeBorderButton,
  LargeSolidButton,
  largeModalButtonLabelClassName,
} from "@/components/ui/button/large-modal-buttons";

export default function NurseSwitchContents({
  onClose,
  selectedGroupId,
  selectedYear,
  selectedMonth,
  nurseData: providedNurseData,
  precepteePeriods: providedPrecepteePeriods,
  shiftsData,
  isUseMid = false,
}: {
  onClose: () => void;
  selectedGroupId: string;
  selectedYear?: number;
  selectedMonth?: number;
  nurseData?: NurseResponse[];
  precepteePeriods?: PrecepteePeriodItem[];
  shiftsData?: ShiftReturnType;
  isUseMid?: boolean;
}) {
  const [isOpen, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const loginUser = useLoginUser();
  const isAdmin = useIsAdmin();

  // 간호사 데이터 조회
  const { data: fetchedNurseData } = useNurses(selectedGroupId, {
    year: selectedYear,
    month: selectedMonth,
    enabled: !providedNurseData,
  });
  const { data: fetchedPrecepteePeriods } = usePrecepteePeriodsQuery({
    groupId: selectedGroupId,
    year: selectedYear,
    month: selectedMonth,
    enabled: !providedPrecepteePeriods,
  });
  const nurseData = useMemo(
    () =>
      applyPrecepteePeriodsToNurses(
        providedNurseData ?? fetchedNurseData,
        providedPrecepteePeriods ?? fetchedPrecepteePeriods?.items
      ),
    [
      fetchedNurseData,
      fetchedPrecepteePeriods?.items,
      providedNurseData,
      providedPrecepteePeriods,
    ]
  );

  // 활성/비활성 리스트 상태
  const [activeList, setActiveList] = useState<NurseWithRelation[]>([]);
  const [inactiveList, setInactiveList] = useState<NurseWithRelation[]>([]);

  // 선택 상태를 관리하는 상태
  const [selectedNurses, setSelectedNurses] = useState<Set<string>>(new Set());

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // 데이터 초기화
  useEffect(() => {
    if (nurseData) {
      const withRelations = linkPreceptorRelations(nurseData);
      setActiveList(withRelations.filter((n) => n.active === 1));
      setInactiveList(withRelations.filter((n) => n.active === 0));
      const inactive = withRelations.filter((n) => n.active === 0);

      // 초기 비활성 간호사들을 선택 상태로 설정
      const inactiveIds = new Set(inactive.map((n) => n.nurse_id));
      setSelectedNurses(inactiveIds);
    }
  }, [nurseData]);

  // 검색 관련 상태
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [highlightedNurseId, setHighlightedNurseId] = useState<string | null>(
    null
  );

  // 하이라이트된 요소로 스크롤
  useEffect(() => {
    if (highlightedNurseId && scrollContainerRef.current) {
      setTimeout(() => {
        const element = scrollContainerRef.current?.querySelector(
          `[data-nid="${highlightedNurseId}"]`
        ) as HTMLElement;

        if (element && scrollContainerRef.current) {
          const container = scrollContainerRef.current;
          const elementRect = element.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const scrollTop = container.scrollTop;
          const scrollPosition =
            scrollTop +
            (elementRect.top - containerRect.top) -
            container.clientHeight / 2 +
            elementRect.height / 2;

          container.scrollTo({
            top: Math.max(0, scrollPosition),
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [highlightedNurseId]);

  // 정렬 상태 (로컬 정렬만, 서버 저장 안 함)
  const [sortConfig, setSortConfig] = useState<{
    key: "experience" | "grade" | "name" | null;
    order: "asc" | "desc" | null;
  }>({ key: null, order: null });

  // 토글 함수를 useCallback으로 감싸기
  const toggleNurseSelection = useCallback(
    (nurseId: string) => {
      const newSelected = new Set(selectedNurses);
      if (newSelected.has(nurseId)) {
        newSelected.delete(nurseId); // 선택 해제
      } else {
        newSelected.add(nurseId); // 선택 추가
      }
      setSelectedNurses(newSelected);
    },
    [selectedNurses]
  );

  // 정렬 핸들러
  const handleSort = useCallback((key: "experience" | "grade" | "name") => {
    setSortConfig((prev) => {
      const isSameKey = prev.key === key;
      let nextOrder: "asc" | "desc" | null = "asc";
      if (isSameKey) {
        if (prev.order === "asc") nextOrder = "desc";
        else if (prev.order === "desc") nextOrder = null;
      }

      const nextConfig = { key: nextOrder ? key : null, order: nextOrder };

      if (nextConfig.key && nextConfig.order) {
        const { key: sortKey, order } = nextConfig;
        const sorter = (a: NurseWithRelation, b: NurseWithRelation) =>
          compareNurses(a, b, sortKey, order);

        // 활성/비활성 리스트 정렬
        setActiveList((prev) => [...prev].sort(sorter));
        setInactiveList((prev) => [...prev].sort(sorter));
      }

      return nextConfig;
    });
  }, []);

  // 저장 Mutation
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      // selectedNurses에 포함된 간호사는 비활성(0), 나머지는 활성(1)
      const allNursesData = [...activeList, ...inactiveList];

      const updatedNurses = unlinkPreceptorRelations(allNursesData).map(
        (n) => ({
          ...n,
          active: selectedNurses.has(n.nurse_id) ? 0 : 1,
        })
      );

      await saveChangeFullNurseSchema(
        updatedNurses,
        isAdmin ? selectedGroupId : undefined,
        loginUser?.office_id ?? null
      );
    },
    onSuccess: () => {
      toast.success("비활성 근무자 설정이 저장되었습니다.");
      setOpen(false);
      const groupId = isAdmin ? selectedGroupId : "";
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.nurses.listScope(groupId),
      });
      onClose();
    },
    onError: (error) => {
      console.error(error);
      toast.error("저장에 실패했습니다.");
    },
  });

  // 저장 버튼 클릭
  const handleSaveClick = () => {
    setOpen(true);
  };

  // 검색 실행
  const handleSearch = useCallback(() => {
    if (!searchTerm.trim()) {
      setHighlightedNurseId(null);
      return;
    }

    const foundActive = activeList.find((n) => n.name === searchTerm.trim());
    const foundInactive = inactiveList.find(
      (n) => n.name === searchTerm.trim()
    );
    const found = foundActive || foundInactive;

    if (found) {
      setHighlightedNurseId(found.nurse_id);
      toast.info(`'${found.name}' 간호사를 찾았습니다.`);
    } else {
      setHighlightedNurseId(null);
      toast.error(`'${searchTerm}' 검색 결과가 없습니다.`);
    }
  }, [searchTerm, activeList, inactiveList]);

  // 엔터키 검색
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // 활성/비활성을 합친 전체 리스트
  const allNurses = useMemo(() => {
    return [...activeList, ...inactiveList];
  }, [activeList, inactiveList]);

  // 비활성 근무자 설정: ManagerTable/NurseTableRow 컬럼 순서와 동일 (입사·재직·종료·수정·원티드 열 제외)
  const customHeaderList = useMemo(() => {
    return [
      { label: "", width: COLUMN_WIDTHS.dragHandle },
      { label: "checkbox", width: COLUMN_WIDTHS.checkbox },
      { label: "No.", width: COLUMN_WIDTHS.no },
      { label: "사번", width: COLUMN_WIDTHS.empNum },
      { label: "이름", width: COLUMN_WIDTHS.name },
      { label: "직종", width: COLUMN_WIDTHS.role },
      { label: "Grade", width: COLUMN_WIDTHS.grade },
      { label: "경력", width: COLUMN_WIDTHS.experience },
      { label: "프리셉터", width: COLUMN_WIDTHS.preceptor },
      { label: "근무형태", width: COLUMN_WIDTHS.fixedShift },
      { label: "주말 휴무", width: COLUMN_WIDTHS.weekendOff },
      { label: "메모", width: COLUMN_WIDTHS.memo },
    ];
  }, []);

  const totalCount = activeList.length + inactiveList.length;
  const inactiveCount = selectedNurses?.size ?? 0;
  const activeCount = totalCount - inactiveCount;

  return (
    <>
      <CustomModal.Confirm
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        onConfirm={mutateAsync}
        isPending={isPending}
        pendingMsg={MSG.ACTIVE_SETTING_SAVE}
        title="비활성 근무자 설정을 저장할까요?"
      />
      <div ref={scrollContainerRef} className="flex-1 min-h-0 ">
        <div className="flex-shrink-0 bg-white z-20 sticky top-0">
          <div className="px-6 pt-[15px] pb-[12px]">
            <h2 className="text-[24px] font-pretendard font-bold text-[#0E59C6] text-center mb-[7px]">
              비활성 근무자 설정
            </h2>
          </div>

          {/* 상단 정보 및 검색 */}
          <div className="flex flex-col px-10 w-full">
            <div className="flex items-center justify-between mb-[25px]">
              <div className="flex items-center gap-2 ml-8">
                <img
                  src={images.icon_person_black}
                  alt=""
                  className="w-3 h-3"
                />
                <span className="text-[14px] font-bold font-pretendard">
                  전체 {totalCount}명 /활성 {activeCount}명/{" "}
                  <span className="text-sig">비활성 {inactiveCount}명 </span>
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-pretendard text-[16px] text-text-main2">
                  비활성 처리할 근무자를 선택 후 ‘설정 완료’를 눌러주세요.
                </span>
              </div>

              <div className="flex items-center gap-2">
                <SearchInput
                  placeholder="이름으로 검색"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  onSearch={handleSearch}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1 pb-4">
          <div className="flex flex-col px-10 w-full">
            <div className="flex flex-col gap-3">
              {/* 활성 근무자 리스트 */}
              <div className="flex flex-col">
                <ManagerTable
                  teamName="전체 근무자"
                  nurseDataWithRelation={allNurses}
                  selectedGroupId={selectedGroupId}
                  activeNurseIdForSideProfile={highlightedNurseId}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  disableRowEdit={true}
                  hideEditColumn={true}
                  enableSelection={true}
                  selectedNurses={selectedNurses}
                  onToggleSelection={toggleNurseSelection}
                  customHeaderList={customHeaderList}
                  shiftsData={shiftsData}
                  isUseMid={isUseMid}
                />
              </div>{" "}
              <div className="flex-1 flex justify-end items-end mr-[20px]">
                <span className="font-pretendard text-[15px] text-muted-foreground">
                  *비활성된 근무자는 근무표에 반영되지 않습니다.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* 하단 버튼 */}
      <div className=" p-6 flex gap-3 justify-center sticky bottom-[-20px] flex-shrink-0 bg-white">
        <div className="w-full">
          <div className="p-6 pt-0 flex gap-3 justify-center sticky bottom-[-20px] flex-shrink-0 bg-background before:content-[''] before:absolute before:-top-10 before:left-0 before:w-full before:h-10 before:bg-gradient-to-t before:from-background before:to-transparent">
            <LargeBorderButton onClick={onClose}>
              <span className={largeModalButtonLabelClassName}>닫기</span>
            </LargeBorderButton>
            <LargeSolidButton onClick={handleSaveClick}>
              <span className={largeModalButtonLabelClassName}>설정완료</span>
            </LargeSolidButton>
          </div>
        </div>
      </div>
    </>
  );
}
